import { useNavigate } from "react-router-dom";
import { CiStatusBadge } from "@/components/ci/status-badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatPerfMs, PERF_BACKEND_LABELS, perfRows } from "@/lib/restore-perf";
import type { CiTestResult } from "@/lib/types";
import { cn } from "@/lib/utils";

// The restore-perf report for a `bench cryod perf run` CI run: one row per
// backend × locality with the restore-time percentiles bench measured (from
// cryod's restore-duration annotation) and the coefficient of variation.
// Only rendered for runs whose rows carry the perf tags.
export function RestorePerfTable({ results, runId }: { results: CiTestResult[]; runId: string }) {
  const navigate = useNavigate();
  const rows = perfRows(results);
  if (rows.length === 0) return null;
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Restore performance</CardTitle>
        <p className="text-xs text-muted-foreground">
          Timed restores of one checkpoint per backend; same-node and cross-node. Variance is the
          sample stdev as a percent of the mean.
        </p>
      </CardHeader>
      <CardContent className="overflow-x-auto p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Backend</TableHead>
              <TableHead>Locality</TableHead>
              <TableHead className="text-right">n</TableHead>
              <TableHead className="text-right">p50</TableHead>
              <TableHead className="text-right">p95</TableHead>
              <TableHead className="text-right">p99</TableHead>
              <TableHead className="text-right">Variance</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.resultId}
                className={cn(
                  "cursor-pointer",
                  row.status === "fail" && "border-l-2 border-l-rose-500",
                )}
                onClick={() => navigate(`/ci/${runId}/tests/${row.resultId}`)}
              >
                <TableCell className="font-medium">
                  {PERF_BACKEND_LABELS[row.backend] ?? row.backend}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {row.locality === "same"
                    ? "same node"
                    : row.locality === "cross"
                      ? "cross node"
                      : "-"}
                </TableCell>
                <TableCell className="text-right font-mono text-sm">{row.n || "-"}</TableCell>
                <TableCell className="text-right font-mono text-sm">
                  {formatPerfMs(row.p50Ms)}
                </TableCell>
                <TableCell className="text-right font-mono text-sm">
                  {formatPerfMs(row.p95Ms)}
                </TableCell>
                <TableCell className="text-right font-mono text-sm">
                  {formatPerfMs(row.p99Ms)}
                </TableCell>
                <TableCell className="text-right font-mono text-sm">
                  {row.variancePct === null ? "-" : `${row.variancePct.toFixed(1)}%`}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <CiStatusBadge status={row.status} />
                    {row.tierMismatch > 0 && (
                      <span className="text-xs text-amber-500">
                        {row.tierMismatch} wrong-tier sample(s) excluded
                      </span>
                    )}
                    {row.error && (
                      <span className="max-w-md truncate text-xs text-rose-500">{row.error}</span>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
